import React from 'react';
import PropTypes from 'prop-types';
//Componentes Material
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
//Icones
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
//Componentes
import ConfirmDialog from '../ConfirmDialog';
import QuestaoEscolhaOpcaoTexto from './QuestaoEscolhaOpcaoTexto';
import QuestaoEscolhaOpcaoValor from './QuestaoEscolhaOpcaoValor';
//Modelos
import QuestionarioOpcaoResposta from '../../models/QuestionarioOpcaoResposta';
import ErroQuestionarioOpcao from '../../models/ErroQuestionarioOpcao';

/** Tipo de opção contendo um campo de texto e um campo de imagem */
const TIPO_OPCAO_TEXTO = 'texto';
/** Tipo de opção contendo um campo que aceita apenas valores numéricos */
const TIPO_OPCAO_NUMERICO = 'numerico';

const TIPOS_OPCAO = {
    TIPO_OPCAO_TEXTO,
    TIPO_OPCAO_NUMERICO
};

/**
 * Componente para tratar os dados de uma opção resposta pré-definida de uma questão
 */
class QuestaoEscolhaOpcao extends React.Component {
    constructor(props)  {
        super(props);
        //Bindings
        this.handleOpcaoChange = this.handleOpcaoChange.bind(this);
        this.handleDeleteClick = this.handleDeleteClick.bind(this);
        this.handleDialogClose = this.handleDialogClose.bind(this);
        //Estado inicial
        this.state = {
            confirmarExclusao: false //Indicação se o dialog de exclusão deve ser exibido
        };
    }

    /**
     * Trata a alteração no corpo da opção de resposta
     * @param {QuestionarioOpcaoResposta} opcao Opção de resposta com as alterações recebidas
     */
    handleOpcaoChange(opcao) {
        //Dispara o evento de alteração
        this.dispararEventoChange(opcao);
    }

    /**
     * Trata o click no botão de exclusão
     */
    handleDeleteClick() {
        if (this.props.opcao.descricao || this.props.opcao.imagem || this.props.opcao.novaImagem) {
            //Caso possua uma alteração, exibe o dialog
            this.setState({ confirmarExclusao: true });
        } else {
            //Dispara o evento de exclusão
            this.dispararEventoDelete(this.props.opcao);
        }
    }

    /**
     * Trata a escolha do usuário no dialog de confirmação
     * @param {Boolean} confirmacao Indica se o usuário confirmou a exclusão
     */
    handleDialogClose(confirmacao) {
        //Caso o usuário tenha confirmado
        if (confirmacao) {
            //Dispara o evento de exclusão
            this.dispararEventoDelete(this.props.opcao);
        } else {
            //Fecha o dialog
            this.setState({ confirmarExclusao: false });
        }
    }

    /**
     * Dispara o evento de alteração da opção de resposta
     * @param {QuestionarioOpcaoResposta} opcao Objeto opção com os dados alterados
     */
    dispararEventoChange(opcao) {
        //Verifica se o evento está preenchido
        if (!this.props.readOnly && this.props.onChange)
            this.props.onChange(opcao);
    }

    /**
     * Dispara o evento de exclusão da opção de resposta
     * @param {QuestionarioOpcaoResposta} opcao Objeto resposta com os dados atuais
     */
    dispararEventoDelete(opcao) {
        if (this.props.onDelete)
            this.props.onDelete(opcao);
    }

    /**
     * Verifica se o componente deve ser atualizado e renderizado novamente
     * @param {Object} nextProps Valor a ser recebido ao atualizar o componente
     * @param {QuestionarioOpcaoResposta} nextProps.opcao Opção de resposta que o componente representa
     * @param {Object} nextState Valor recebido ao atualizar o estado do componente
     * @param {Boolean} nextState.confirmarExclusao Indica se o dialog de exclusão está visível
     * @returns {Boolean} Retorna verdadeiro quando o componente deve ser atualizado
     */
    shouldComponentUpdate(nextProps, nextState) {
        return nextState.confirmarExclusao !== this.state.confirmarExclusao
            || nextProps.tipoOpcao !== this.props.tipoOpcao
            || !ErroQuestionarioOpcao.compare(nextProps.erros, this.props.erros)
            || !QuestionarioOpcaoResposta.compare(nextProps.opcao, this.props.opcao);
    }

    /**
     * Renderiza o corpo da opção de resposta de acordo com o tipo informado
     * @returns {Object} Retorna o corpo da opção de resposta de acordo
     */
    renderCorpoOpcao() {
        const { tipoOpcao, opcao, erros, focoAutomatico, readOnly } = this.props;
        if (tipoOpcao === TIPO_OPCAO_TEXTO)
            return (<QuestaoEscolhaOpcaoTexto readOnly={readOnly} opcao={opcao} erros={erros} focoAutomatico={focoAutomatico} onChange={this.handleOpcaoChange} />);
        else if (tipoOpcao === TIPO_OPCAO_NUMERICO)
            return (<QuestaoEscolhaOpcaoValor readOnly={readOnly} opcao={opcao} erros={erros} focoAutomatico={focoAutomatico} onChange={this.handleOpcaoChange} />);
    }

    render() {
        const { confirmarExclusao } = this.state;
        const { readOnly } = this.props;
        
        return (
            <Grid container item xs={12} spacing={0} wrap="nowrap" >
                { this.renderCorpoOpcao() }
                {
                    !readOnly &&
                        <Grid item >
                            <IconButton color="primary" id="excluir-opcao" onClick={this.handleDeleteClick} tabIndex={-1} >
                                <HighlightOffIcon />
                            </IconButton>
                        </Grid>
                }
                <ConfirmDialog titulo="Deseja excluir essa opção?" open={confirmarExclusao} onClose={this.handleDialogClose} />
            </Grid>
        );
    }
}

QuestaoEscolhaOpcao.propTypes = {
    /** Dados da opção de resposta a ser editada */
    opcao: PropTypes.instanceOf(QuestionarioOpcaoResposta).isRequired,
    /** Mensagens de erro da opção de resposta */
    erros: PropTypes.instanceOf(ErroQuestionarioOpcao),
    /** Indica se o componente terá o foco ao ser criado */
    focoAutomatico: PropTypes.bool,
    /** Tipo da opção de resposta */
    tipoOpcao: PropTypes.oneOf([ TIPO_OPCAO_TEXTO, TIPO_OPCAO_NUMERICO ]).isRequired,
    /** Identifica se o componente é somente leitura e não permite alterações */
    readOnly: PropTypes.bool,
    /** Evento disparado quando alguma alteração é realizada na reposta */
    onChange: PropTypes.func,
    /** Evento disparado quando a opção é excluída */
    onDelete: PropTypes.func
};

QuestaoEscolhaOpcao.defaultProps = {
    erros: new ErroQuestionarioOpcao(),
    readOnly: false
};

export default QuestaoEscolhaOpcao;
export { TIPOS_OPCAO, TIPO_OPCAO_TEXTO, TIPO_OPCAO_NUMERICO };